/* =====================================================================
 * liveAgentStore — 라이브 에이전트 전용 상태 관리 (chatStore와 완전 분리)
 * ===================================================================== */

import { create } from 'zustand';

export type LiveAgentName =
  | 'Rebo'
  | 'WebAgent'
  | 'ImageAgent'
  | 'VideoAgent'
  | 'DocumentAgent'
  | 'YoutubeAgent'
  | 'AnalysisAgent';

export interface LiveAgentMessage {
  id: string;
  author: LiveAgentName | 'user';
  text: string;
  timestamp: number;
  audioData?: string;
  transcript?: string;
  /** DB에 저장 완료 여부 */
  persisted?: boolean;
  /**
   * 이 (assistant) 메시지가 속한 *턴* 의 message_id.
   *
   * AI 서버가 턴마다 부여해 ``turn_start`` WS 프레임으로 내려주는 값
   * (``{session_id}::tN``). 영속화 시 ``metadata.completedData.message_id`` 로
   * 저장돼, usage ledger(턴별 message_id) ↔ 활동내역 본문 매칭의 키가 된다.
   * 통합 에이전트의 스트림 message_id 와 동일한 역할.
   */
  turnMessageId?: string;
  /**
   * 이 (assistant) 메시지의 같은 turn 동안 도구가 push 한 카드들.
   *
   * 라이브 모델이 ``web_search`` / ``use_<provider>`` 같은 wrapper tool 을
   * 호출할 때 백엔드(``ai/.../live/cards/publisher.py``) 가
   * ``cards.update`` WebSocket 메시지를 보낸다.  WS 페이로드에는 어느
   * message_id 와 묶이는지 정보가 없지만, 라이브 세션의 turn 모델 상
   * "지금 발화 중인 마지막 assistant 메시지" 가 곧 그 카드의 소속 메시지다.
   * useLiveAgentSession 의 cards.update 핸들러가 그 매핑을 수행한다.
   */
  cards?: LiveAgentCard[];
}

/**
 * 라이브 에이전트가 도구 호출 결과를 카드 형태로 화면에 띄울 때 사용하는
 * 직렬화 형식. 서버 ``ai/.../live/cards/models.py`` 의 ``CardEvent`` 와 1:1
 * 대응하며, snake_case 필드명을 그대로 받는다 (Pydantic 기본 dump).
 *
 * - ``id``: dedupe key. 같은 id 의 카드는 마지막 push 가 이긴다.
 * - ``kind``: UI 의 시각 분기 (`paper` | `article` | `web` | `plugin` | `ack`).
 * - ``source_label``: 카드 우상단 출처 라벨 (`"arXiv"`, `"Gmail"` 등).
 * - ``meta``: 도구별 자유 필드. UI 가 모르는 키는 안전하게 무시한다.
 */
export type LiveAgentCardKind =
  | 'paper'
  | 'article'
  | 'web'
  | 'plugin'
  | 'ack'
  | 'content';

export interface LiveAgentCard {
  id: string;
  kind: LiveAgentCardKind;
  title: string;
  subtitle?: string | null;
  url?: string | null;
  source_label?: string | null;
  snippet?: string | null;
  /**
   * 본문 카드(``kind === 'content'``)의 전체 내용. ``show_on_screen`` 툴이
   * 채운다. 코드/마크다운/정형 텍스트처럼 음성으로 읽기보다 화면에 보여줄
   * 산출물. 링크형 카드는 비어 있고 ``snippet`` 을 쓴다.
   */
  body?: string | null;
  /** ``body`` 렌더 방식: ``'code'`` | ``'markdown'`` | ``'plain'``. */
  body_format?: string | null;
  /** ``body_format === 'code'`` 일 때 구문 강조 언어(예: ``'python'``). */
  language?: string | null;
  /**
   * 헤더 좌측의 작은 도메인 favicon. 보통
   * ``https://www.google.com/s2/favicons?domain=...&sz=64``.
   * 없거나 로드 실패 시 lucide kind 아이콘으로 폴백.
   */
  favicon_url?: string | null;
  /**
   * 카드 본문 좌측에 표시할 큰 썸네일. 검색 grounding 결과에서는 거의
   * 비어 있지만, 향후 plugin tool 이 og:image 같은 값을 채울 수 있다.
   */
  thumbnail_url?: string | null;
  meta?: Record<string, unknown>;
  created_at?: number;
}

export type LiveSessionStatus =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'error'
  | 'disconnected';

export interface GeminiVoice {
  name: string;
  style: string;
}

export const GEMINI_VOICES: GeminiVoice[] = [
  { name: 'Zephyr', style: 'Bright' },
  { name: 'Puck', style: 'Upbeat' },
  { name: 'Charon', style: 'Informative' },
  { name: 'Kore', style: 'Firm' },
  { name: 'Fenrir', style: 'Excitable' },
  { name: 'Leda', style: 'Youthful' },
  { name: 'Orus', style: 'Firm' },
  { name: 'Aoede', style: 'Breezy' },
  { name: 'Callirrhoe', style: 'Easy-going' },
  { name: 'Autonoe', style: 'Bright' },
  { name: 'Enceladus', style: 'Breathy' },
  { name: 'Iapetus', style: 'Clear' },
  { name: 'Umbriel', style: 'Easy-going' },
  { name: 'Algieba', style: 'Smooth' },
  { name: 'Despina', style: 'Smooth' },
  { name: 'Erinome', style: 'Clear' },
  { name: 'Algenib', style: 'Gravelly' },
  { name: 'Rasalgethi', style: 'Informative' },
  { name: 'Laomedeia', style: 'Upbeat' },
  { name: 'Achernar', style: 'Soft' },
  { name: 'Alnilam', style: 'Firm' },
  { name: 'Schedar', style: 'Even' },
  { name: 'Gacrux', style: 'Mature' },
  { name: 'Pulcherrima', style: 'Forward' },
  { name: 'Achird', style: 'Friendly' },
  { name: 'Zubenelgenubi', style: 'Casual' },
  { name: 'Vindemiatrix', style: 'Gentle' },
  { name: 'Sadachbia', style: 'Lively' },
  { name: 'Sadaltager', style: 'Knowledgeable' },
  { name: 'Sulafat', style: 'Warm' },
];

interface LiveAgentState {
  sessionStatus: LiveSessionStatus;
  sessionId: string | null;
  /** chatStore에 생성된 대화 ID (사이드바 히스토리용) */
  conversationId: string | null;
  messages: LiveAgentMessage[];
  activeAgent: LiveAgentName | null;
  /**
   * 에이전트가 실제로 음성을 출력(발화)하고 있는지.
   *
   * - `useLiveAgentSession.feedAudio()` 가 호출될 때마다 `true` 로 설정되고,
   *   마지막 audio chunk 이후 짧은 silence debounce 를 거쳐 `false` 로 돌아온다.
   * - `turnComplete` 수신 시에도 즉시 `false`.
   * - Dock 의 Rebo 아이콘 lift/호흡 애니메이션 트리거 용도로 사용된다.
   */
  agentSpeaking: boolean;
  sendTextFn: ((text: string) => void) | null;
  stopSessionFn: (() => void) | null;
  resumeSessionFn: (() => Promise<void>) | null;

  muted: boolean;
  voiceName: string;
  /**
   * 에이전트 발화 재생 속도 배율 (1.0 = 원본). 0.8 ~ 1.5 권장.
   * 클라이언트 측 PCM worklet (`pcm-player-processor.js`) 에서 readIndex
   * 전진 속도 + 선형 보간으로 적용. 서버 SpeechConfig 에는 speed 필드가
   * 없어 (Live API 미지원) client-side 로 처리한다.
   */
  playbackRate: number;
  settingsOpen: boolean;

  /** 현재 세션에서 실제 연결된 플러그인 (서버에서 session_ready로 전달) */
  connectedPlugins: string[];

  /**
   * 카드가 assistant 메시지가 등장하기 *전에* 도착한 경우 임시로 보관하는
   * 버킷.  라이브에서는 보통 흐름이
   *
   *     user 발화 → web_search 호출 (몇 초) → 카드 push 도착 → 모델 발화 시작
   *
   * 이라 카드가 메시지보다 먼저 올 수 있다.  새 assistant 메시지가
   * 추가될 때 이 버킷이 그 메시지의 ``cards`` 로 이관되며, 새 user 메시지
   * 가 들어오면 잔여물은 버린다 (이전 turn 의 늦은 카드일 수 있음).
   */
  pendingTurnCards: LiveAgentCard[];

  setSessionStatus: (status: LiveSessionStatus) => void;
  setSessionId: (id: string | null) => void;
  setConversationId: (id: string | null) => void;
  setMessages: (messages: LiveAgentMessage[]) => void;
  addMessage: (msg: LiveAgentMessage) => void;
  appendToLastMessage: (text: string) => void;
  appendToMessageById: (id: string, text: string) => void;
  replaceMessageText: (id: string, text: string) => void;
  markMessagePersisted: (id: string) => void;
  setActiveAgent: (agent: LiveAgentName | null) => void;
  setAgentSpeaking: (speaking: boolean) => void;
  setSendTextFn: (fn: ((text: string) => void) | null) => void;
  setStopSessionFn: (fn: (() => void) | null) => void;
  setResumeSessionFn: (fn: (() => Promise<void>) | null) => void;
  setMuted: (v: boolean) => void;
  setVoiceName: (v: string) => void;
  setPlaybackRate: (v: number) => void;
  setSettingsOpen: (v: boolean) => void;
  setConnectedPlugins: (plugins: string[]) => void;
  /**
   * ``cards.snapshot`` 수신 — 모든 메시지 카드 + pendingTurnCards 정리 후
   * 들어온 카드를 마지막 assistant 메시지(있으면)에 부착하고, 없으면
   * pendingTurnCards 에 보관.
   */
  setCards: (cards: LiveAgentCard[]) => void;
  /**
   * ``cards.update`` 수신 — id 기준 dedupe 후 항상 ``pendingTurnCards`` 에
   * 보관한다.  현재 화면 위에서 *진행 중인* assistant 메시지에는 부착하지
   * 않는다.  이유:
   *
   * 라이브 모델이 다음 두 turn 시퀀스를 만들 수 있다:
   *
   *   turn 1) 텍스트 \"잠시만요…\" + function_call(web_search)  → message m1
   *   turn 2) function_response 받은 뒤 검색 결과 종합 발화        → message m2
   *
   * 카드는 web_search wrapper 가 yield 한 직후 push 된다 — 즉 turn 1
   * (m1) 이 store 에 막 들어간 시점이다. 만약 그 시점에 m1 의 cards 에
   * 부착하면, 카드는 \"잠시만요\" 같은 추측 발화에 붙고 진짜 답변(m2) 에는
   * 안 붙는다 (사용자가 본 두 번째 이미지 시나리오와 정확히 일치).
   *
   * 따라서 cards.update 는 항상 pending 으로만 보내고, 이후 ``addMessage``
   * 가 *다음 새 assistant 메시지* 를 만들 때 그 메시지의 ``cards`` 로 흡수
   * 한다. 새 user 발화가 들어오면 (turn 종료) pending 잔여물은 폐기된다.
   */
  appendCards: (cards: LiveAgentCard[]) => void;
  /** 모든 메시지의 cards 와 pendingTurnCards 를 비운다. */
  clearCards: () => void;
  clearSession: () => void;
}

export const useLiveAgentStore = create<LiveAgentState>((set) => ({
  sessionStatus: 'idle',
  sessionId: null,
  conversationId: null,
  messages: [],
  activeAgent: null,
  agentSpeaking: false,
  sendTextFn: null,
  stopSessionFn: null,
  resumeSessionFn: null,
  muted: false,
  voiceName: 'Puck',
  playbackRate: 1.0,
  settingsOpen: false,
  connectedPlugins: [],
  pendingTurnCards: [],

  setSessionStatus: (status) => set({ sessionStatus: status }),
  setSessionId: (id) => set({ sessionId: id }),
  setConversationId: (id) => set({ conversationId: id }),
  setSendTextFn: (fn) => set({ sendTextFn: fn }),
  setStopSessionFn: (fn) => set({ stopSessionFn: fn }),
  setResumeSessionFn: (fn) => set({ resumeSessionFn: fn }),
  setMuted: (v) => set({ muted: v }),
  setVoiceName: (v) => set({ voiceName: v }),
  setPlaybackRate: (v) => {
    // 0.5 ~ 2.0 범위로 강제 — worklet 의 RATE_MIN/RATE_MAX 와 동기.
    const clamped = Math.max(0.5, Math.min(2.0, Number(v) || 1.0));
    set({ playbackRate: clamped });
  },
  setSettingsOpen: (v) => set({ settingsOpen: v }),
  setConnectedPlugins: (plugins) => set({ connectedPlugins: plugins }),
  setMessages: (messages) => set({ messages }),
  addMessage: (msg) =>
    set((s) => {
      // 새 assistant 메시지가 등장하면 그동안 모아둔 pendingTurnCards 를
      // 이 메시지의 cards 로 흡수.  새 user 메시지가 들어오면 (turn 종료
      // 시점) 잔여 pendingTurnCards 는 이전 turn 의 늦은 카드일 수 있어
      // 폐기한다.
      if (msg.author === 'user') {
        return {
          messages: [...s.messages, msg],
          pendingTurnCards: [],
        };
      }
      const newMsg: LiveAgentMessage =
        s.pendingTurnCards.length > 0
          ? { ...msg, cards: [...(msg.cards ?? []), ...s.pendingTurnCards] }
          : msg;
      return {
        messages: [...s.messages, newMsg],
        pendingTurnCards: [],
      };
    }),
  appendToLastMessage: (text) =>
    set((s) => {
      const msgs = [...s.messages];
      if (msgs.length > 0) {
        const last = { ...msgs[msgs.length - 1] };
        last.text += text;
        msgs[msgs.length - 1] = last;
      }
      return { messages: msgs };
    }),
  appendToMessageById: (id, text) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, text: m.text + text } : m,
      ),
    })),
  replaceMessageText: (id, text) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, text } : m,
      ),
    })),
  markMessagePersisted: (id) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, persisted: true } : m,
      ),
    })),
  setActiveAgent: (agent) => set({ activeAgent: agent }),
  setAgentSpeaking: (speaking) => set({ agentSpeaking: speaking }),
  setCards: (incoming) =>
    set((s) => {
      // snapshot 은 turn 시작/재연결 시 한 번 동기화하는 의미라 모든
      // 메시지 카드와 pending 을 비운 뒤 새로 분배한다.
      const cleared = s.messages.map((m) =>
        m.cards && m.cards.length > 0 ? { ...m, cards: [] } : m,
      );
      if (incoming.length === 0) {
        return { messages: cleared, pendingTurnCards: [] };
      }
      const lastAssistantIdx = findLastAssistantIndex(cleared);
      if (lastAssistantIdx >= 0) {
        const target = cleared[lastAssistantIdx];
        cleared[lastAssistantIdx] = {
          ...target,
          cards: incoming.slice(),
        };
        return { messages: cleared, pendingTurnCards: [] };
      }
      return { messages: cleared, pendingTurnCards: incoming.slice() };
    }),
  appendCards: (incoming) =>
    set((s) => {
      if (incoming.length === 0) return {};
      // 항상 pending 으로만 보낸다. 현재 \"진행 중\" 인 마지막 assistant
      // 메시지에는 부착하지 않는다. 이유는 인터페이스 docstring 참조 —
      // 라이브 모델이 도구 호출 turn 에서 추측 발화 (\"잠시만요…\") 를
      // 먼저 emit 한 경우, 카드를 그 메시지에 붙이면 진짜 답변 메시지가
      // 카드 없이 비어버린다. 다음 ``addMessage(assistant)`` 가 pending 을
      // 흡수하므로 카드는 항상 검색 결과 종합 발화에 매핑된다.
      return {
        pendingTurnCards: mergeCardsById(s.pendingTurnCards, incoming),
      };
    }),
  clearCards: () =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.cards && m.cards.length > 0 ? { ...m, cards: [] } : m,
      ),
      pendingTurnCards: [],
    })),
  clearSession: () =>
    set({
      sessionStatus: 'idle',
      sessionId: null,
      conversationId: null,
      messages: [],
      activeAgent: null,
      agentSpeaking: false,
      connectedPlugins: [],
      pendingTurnCards: [],
    }),
}));

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

function findLastAssistantIndex(messages: LiveAgentMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i].author !== 'user') return i;
  }
  return -1;
}

function mergeCardsById(
  existing: LiveAgentCard[],
  incoming: LiveAgentCard[],
): LiveAgentCard[] {
  if (incoming.length === 0) return existing;
  const incomingById = new Map(incoming.map((c) => [c.id, c]));
  const next = existing.map((card) => {
    const updated = incomingById.get(card.id);
    if (!updated) return card;
    incomingById.delete(card.id);
    return updated;
  });
  for (const card of incoming) {
    if (incomingById.has(card.id)) {
      next.push(card);
    }
  }
  return next;
}
